import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ContainedButton } from 'components/ContainedButton';
import { useSankeyDiagram } from 'hooks/useSankeyDiagram';
import { SankeyLink, SankeyNode } from 'hooks/useSankeyDiagram/types';
import { TFunction } from 'i18next';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountingType } from 'v2-types/order';

const MOBILE_MAX_DESCRIPTIONS = 6;
const MOBILE_MAX_PARTIES = 6;
const MOBILE_DIAGRAM_HEIGHT = 380;
const MOBILE_DIAGRAM_FONT_SIZE = 10;
const FULL_DIAGRAM_MIN_WIDTH = 600;
const FULL_DIAGRAM_MIN_HEIGHT = 700;

const useStyles = makeStyles((theme: any) => ({
  scrollContainer: {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1),
    },
  },
  diagram: { width: '100%' },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  emptyText: {
    fontWeight: 500,
    color: theme.palette.tab.offselected,
    textAlign: 'center',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
  },
  fullScreenContent: {
    height: '100%',
    padding: theme.spacing(2),
  },
  fullScreenHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  fullScreenTitle: {
    fontWeight: 500,
    color: theme.palette.primary.main,
  },
}));

type SankeyData = {
  nodes: SankeyNode[];
  links: SankeyLink[];
};

const ANCHOR_LOAN = 'anchor:loan';
const ANCHOR_PURCHASE = 'anchor:purchase';
const entryNodeId = (description: string) => `entry:${description}`;
const partyNodeId = (name: string) => `party:${name}`;
const kindNodeId = (kind: string) => `kind:${kind}`;
const aggregateNodeId = (label: string) => `aggregate:${label}`;

const sanitizeLinks = (links: SankeyLink[]): SankeyLink[] => links.filter((link) => {
  const value = Number(link.value);
  return link.source !== link.target && Number.isFinite(value) && value > 0;
});

const keepLinkedNodes = ({ nodes, links }: SankeyData): SankeyData => {
  const used = new Set<string>();
  links.forEach((link) => {
    used.add(link.source as string);
    used.add(link.target as string);
  });
  return { nodes: nodes.filter((node) => used.has(node.id ?? node.name)), links };
};

type AggregateLayerParams = SankeyData & {
  ids: Set<string>;
  max: number;
  label: string;
  redirectTargets: boolean;
};

const aggregateLayer = ({
  nodes,
  links,
  ids,
  max,
  label,
  redirectTargets,
}: AggregateLayerParams): SankeyData => {
  if (ids.size <= max) {
    return { nodes, links };
  }

  const value = new Map<string, number>();
  links.forEach((link) => {
    const source = link.source as string;
    if (!ids.has(source)) { return; }
    value.set(source, (value.get(source) || 0) + (Number(link.value) || 0));
  });

  const kept = new Set(
    [...value.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, max)
      .map(([id]) => id)
  );

  const othersId = aggregateNodeId(label);
  const mergedLinks = new Map<string, SankeyLink>();
  links.forEach((link) => {
    const rawSource = link.source as string;
    const rawTarget = link.target as string;
    const source = ids.has(rawSource) && !kept.has(rawSource) ? othersId : rawSource;
    const target = redirectTargets && ids.has(rawTarget) && !kept.has(rawTarget) ? othersId : rawTarget;
    const key = `${source}||${target}`;
    const existing = mergedLinks.get(key);
    if (existing) {
      existing.value = ((Number(existing.value) || 0) + (Number(link.value) || 0)).toString();
    } else {
      mergedLinks.set(key, { source, target, value: link.value });
    }
  });

  const nextNodes = nodes.filter((node) => {
    const id = node.id ?? node.name;
    return !ids.has(id) || kept.has(id);
  });
  if (nextNodes.filter((node) => (node.id ?? node.name) === othersId).length === 0) {
    nextNodes.push({ id: othersId, name: label, category: 'Association' });
  }

  return { nodes: nextNodes, links: [...mergedLinks.values()] };
};

type BuildOptions = {
  maxDescriptions?: number;
  maxParties?: number;
};

export const buildSankeyData = (
  accounting: AccountingType[],
  t: TFunction,
  options: BuildOptions = {}
): SankeyData => {
  const nodes: SankeyNode[] = [
    { id: ANCHOR_LOAN, name: t('accounting:loan-amount'), category: 'Premium' },
    { id: ANCHOR_PURCHASE, name: t('accounting:purchase-price'), category: 'Premium' },
  ];
  const links: SankeyLink[] = [];
  const descriptionIds = new Set<string>();
  const partyIds = new Set<string>();
  const knownIds = new Set<string>([ANCHOR_LOAN, ANCHOR_PURCHASE]);

  const addNode = (id: string, name: string, category: string) => {
    if (knownIds.has(id)) { return; }
    knownIds.add(id);
    nodes.push({ id, name, category });
  };

  accounting
    .reduce((acc: AccountingType[], item) => {
      if (!acc.some((entry) => entry.description === item.description)) {
        acc.push(item);
      }
      return acc;
    }, [])
    .forEach((entry: AccountingType) => {
      if (entry.kind === 'property' || entry.involved_parties?.length) {
        addNode(entryNodeId(entry.description), entry.description, entry.kind);
        descriptionIds.add(entryNodeId(entry.description));
      }
      if (entry.kind === 'property') {
        if (entry.code.startsWith('loan_amount')) {
          links.push({ source: entryNodeId(entry.description), target: ANCHOR_LOAN, value: entry.amount.toString() });
        }
        if (entry.code.startsWith('purchase_price')) {
          links.push({ source: entryNodeId(entry.description), target: ANCHOR_PURCHASE, value: entry.amount.toString() });
        }
      }

      entry.involved_parties.forEach((party) => {
        addNode(partyNodeId(party.name), party.name, entry.kind);
        addNode(kindNodeId(party.kind), party.kind, 'Association');
        partyIds.add(partyNodeId(party.name));
        links.push({ source: partyNodeId(party.name), target: entryNodeId(entry.description), value: party.amount.toString() });
        links.push({ source: partyNodeId(party.name), target: kindNodeId(party.kind), value: party.amount.toString() });
        links.push({ source: entryNodeId(entry.description), target: kindNodeId(party.kind), value: party.amount.toString() });
      });
    });

  let result: SankeyData = keepLinkedNodes({ nodes, links: sanitizeLinks(links) });

  if (options.maxDescriptions !== undefined) {
    result = aggregateLayer({
      ...result,
      ids: descriptionIds,
      max: options.maxDescriptions,
      label: t('accounting:others'),
      redirectTargets: true,
    });
  }

  if (options.maxParties !== undefined) {
    result = aggregateLayer({
      ...result,
      ids: partyIds,
      max: options.maxParties,
      label: t('accounting:other-parties'),
      redirectTargets: false,
    });
  }

  return result;
};

type SankeyRendererProps = {
  accounting: AccountingType[];
  maxDescriptions?: number;
  maxParties?: number;
  height?: number;
  minWidth?: number;
  fontSize?: number;
};

const SankeyRenderer = ({
  accounting,
  maxDescriptions,
  maxParties,
  height = 600,
  minWidth,
  fontSize = 12,
}: SankeyRendererProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const htmlSankeyDiagramRef = useRef<HTMLDivElement>(null);
  const sankeyData = useMemo(
    () => buildSankeyData(accounting, t, { maxDescriptions, maxParties }),
    [accounting, t, maxDescriptions, maxParties]
  );

  useSankeyDiagram({
    htmlDivElementRef: htmlSankeyDiagramRef,
    sankeyNodes: sankeyData.nodes,
    sankeyLinks: sankeyData.links,
    height,
    fontSize,
  });

  if (!sankeyData.links.length) {
    return (
      <Box className={classes.empty}>
        <Typography variant="body2" className={classes.emptyText}>
          {t('accounting:no-money-flow')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.scrollContainer}>
      <div
        ref={htmlSankeyDiagramRef}
        className={classes.diagram}
        style={minWidth ? { minWidth } : undefined}
      />
    </Box>
  );
};

const SankeyDiagram = ({ accounting }: { accounting: AccountingType[] }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [fullScreenOpen, setFullScreenOpen] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);

  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {isMobile ? (
        <>
          <Box className={classes.actions}>
            <ContainedButton
              text={t('accounting:view-full-diagram')}
              size="small"
              disableElevation
              onClick={() => setFullScreenOpen(true)}
            />
          </Box>

          <SankeyRenderer
            accounting={accounting}
            maxDescriptions={MOBILE_MAX_DESCRIPTIONS}
            maxParties={MOBILE_MAX_PARTIES}
            height={MOBILE_DIAGRAM_HEIGHT}
            fontSize={MOBILE_DIAGRAM_FONT_SIZE}
          />
        </>
      ) : (
        <SankeyRenderer accounting={accounting} />
      )}

      <Dialog fullScreen open={fullScreenOpen} onClose={() => setFullScreenOpen(false)}>
        <DialogContent className={classes.fullScreenContent}>
          <Box className={classes.fullScreenHeader}>
            <Typography variant="h6" className={classes.fullScreenTitle}>
              {t('accounting:money-flow')}
            </Typography>
            <IconButton
              onClick={() => setFullScreenOpen(false)}
              aria-label={t('common:close')}
              size="large"
            >
              <FontAwesomeIcon icon="times" />
            </IconButton>
          </Box>
          <SankeyRenderer
            accounting={accounting}
            height={Math.max(FULL_DIAGRAM_MIN_HEIGHT, viewportHeight - 140)}
            minWidth={FULL_DIAGRAM_MIN_WIDTH}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default memo(SankeyDiagram);
